<template>
    <div class="flex-grow-1">
        <!-- Hotspots Display -->
        <div class="card hotspot-card" @click="dismissPopovers($event)">
            <div v-show="panel.header !== ''" class="card-header">
                <p class="h5 mb-0 panel-header">{{ panel.header }}</p>
            </div>
            <div class="card-body p-0 panel-card-body d-flex flex-column w-100" style="min-height: 40px">
                <div class="rich-text-content p-3" v-show="subheaderIsConfigured" v-html="panel.sub_header"></div>
                <div
                    v-if="
                        panelIsConfigured &&
                        panel.panel_type === 'image_hotspots' &&
                        panel.hotspots_content_type == '2d_image'
                    "
                    class="hotspots-container w-100"
                    :class="{
                        'full-screen-2d d-flex justify-content-center align-items-center': panel.display_full_screen,
                    }"
                >
                    <div class="spots-container" :style="hotspotsContainerStyle">
                        <div
                            v-for="choice in panel.choices"
                            :key="choice.id"
                            :id="'hotspot' + choice.id"
                            class="hotspot"
                            :class="choiceClassArray(choice)"
                            :style="choiceStyle(choice)"
                            @click="selectChoice(choice)"
                            @touchend="touchHotspotEnd($event, choice)"
                            @mouseleave="dismissPopover(choice)"
                            @focus="showChoiceLabel(choice)"
                            @focusout="hideChoiceLabel(choice)"
                            v-on:keyup.enter="selectChoice(choice)"
                            :aria-label="'Interactive hotspot ' + choice.text"
                            :tabindex="hotspotTabIndex"
                        ></div>
                    </div>
                    <img
                        :id="'hotspots-image' + _uid"
                        :src="panel.elements[0].stored_file.signed_url"
                        :alt="panel.elements[0].accessibility_alt_text"
                        :class="{ 'w-100': !panel.display_full_screen }"
                        v-on:load="imageLoaded()"
                    />
                </div>
                <div
                    v-if="(panel && panel.hotspots_content_type == '360_image') || panel.panel_type === '3d_video'"
                    class="w-100 mh-100 flex-grow-1"
                >
                    <hotspots-360
                        v-if="contentReady && panelIsConfigured"
                        :panel="panel"
                        :media-src="fileSource"
                        :choices="panel.choices"
                        v-on:select-choice="selectChoice($event)"
                        v-on:video-ended="handleVideoEnded"
                        :class="{ 'full-screen': panel.display_full_screen }"
                        :loaded.sync="panel.isLoaded"
                        :enable-tabbing="panel.enable_hotspot_tabbing"
                        :show-prompts="!isPreviouslyVisited"
                        :show-loading="showLoader"
                        :zoom-level="zoomLevel"
                        :ref="'hotspots360' + _uid"
                    ></hotspots-360>
                    <div
                        class="w-100 d-flex justify-content-center align-items-center full-screen bg-white"
                        v-if="panel.display_full_screen && !panel.isLoaded"
                    >
                        <div class="custom-spinner" role="status">
                            <span class="sr-only">Loading</span>
                        </div>
                    </div>
                </div>
                <div
                    v-if="showDockedCard"
                    class="full-screen-controls card shadow border-0"
                    :class="{
                        'full-screen-controls-left': panel.position === 'left',
                        'full-screen-controls-right': panel.position !== 'left',
                    }"
                >
                    <div class="card-body px-3 pt-3 pb-2">
                        <div
                            v-if="panel.header || subheaderIsConfigured"
                            class="d-flex"
                            :class="{ 'controls-collapsed': !showFullScreenControls }"
                        >
                            <div class="flex-grow-1">
                                <div v-show="showFullScreenControls">
                                    <h5 v-show="panel.header !== ''" class="mb-2 panel-header">
                                        {{ panel.header }}
                                    </h5>
                                    <div
                                        class="rich-text-content mb-2"
                                        v-show="subheaderIsConfigured"
                                        v-html="panel.sub_header"
                                    ></div>
                                </div>
                            </div>
                            <div class="flex-grow-0 font-weight-bold ml-2" style="margin-right: -5px">
                                <button
                                    class="btn btn-transparent remove collapse-chevron"
                                    role="button"
                                    aria-label="Minimize description text toggle"
                                    @click="toggleFullScreenControls()"
                                >
                                    <span
                                        class="align-middle"
                                        :class="{
                                            'icon-glyphicons-basic-chevron-down': showFullScreenControls,
                                            'icon-glyphicons-basic-chevron-up': !showFullScreenControls,
                                        }"
                                        style="font-size: 115%"
                                    >
                                    </span>
                                </button>
                            </div>
                        </div>
                        <div class="d-flex align-items-center mb-1 mt-2">
                            <div class="flex-grow-1 d-flex mr-3">
                                <div class="flex-grow-0">
                                    <!-- Continue Button -->
                                    <session-footer-btns
                                        v-show="!loading"
                                        @continue="handleContinue()"
                                    ></session-footer-btns>
                                </div>
                            </div>
                            <div class="justify-content-end align-items-end flex-grow-0 d-flex flex-column">
                                <div v-show="showDiscoveryMessage" class="ml-2 nowrap-sm" style="font-size: 90%">
                                    <span
                                        class="icon icon-glyphicons-basic-search feedback-icon text-primary align-middle"
                                    ></span>
                                    <span class="text-secondary align-middle" v-if="choices">
                                        {{ discoveryMessage }}</span
                                    >
                                </div>
                                <div v-show="editMode" :class="{ 'd-flex': editMode }" class="align-items-center my-1">
                                    <a v-if="showSetAngleBtn" class="btn btn-link btn-sm p-0 mr-2" @click="saveInitialCameraAngle()">
                                        <span class="icon icon-glyphicons-basic-view-full icon-mr align-middle"></span>
                                        <span class="align-middle">Set initial camera angle</span>
                                    </a>
                                    <info-popover content="Snap Left" placement="top" :dismiss-on-click="true">
                                        <button
                                            aria-label="Snap content left"
                                            class="btn btn-transparent py-0 px-1"
                                            @click="moveFullScreenControls('left')"
                                        >
                                            <span
                                                aria-hidden="true"
                                                class="icon icon-glyphicons-basic-arrow-thin-left"
                                            ></span>
                                        </button>
                                    </info-popover>
                                    <info-popover
                                        content="Snap Right"
                                        placement="top"
                                        :dismiss-on-click="true"
                                        class="mr-2"
                                    >
                                        <button
                                            aria-label="Snap content right"
                                            class="btn btn-transparent py-0 px-1"
                                            @click="moveFullScreenControls('right')"
                                        >
                                            <span
                                                aria-hidden="true"
                                                class="icon icon-glyphicons-basic-arrow-thin-right"
                                            ></span>
                                        </button>
                                    </info-popover>
                                    <info-popover content="Edit Content" placement="top" :dismiss-on-click="true">
                                        <button
                                            class="btn btn-transparent py-0 text-dark btn-sm nowrap"
                                            @click="handleEdit()"
                                        >
                                            <span
                                                aria-hidden="true"
                                                class="icon icon-glyphicons-basic-square-edit icon-mr align-middle"
                                            ></span>
                                            <span class="align-middle">Edit</span>
                                        </button>
                                    </info-popover>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Choice Action Modal -->
            <base-modal :id="'hotspots-choice-modal' + _uid" :centered="true" :size="choiceModalSize">
                <template slot="title">
                    <span
                        v-show="currentChoice && currentChoice.correct === true"
                        class="icon modal-icon icon-glyphicons-basic-circle-empty-check feedback-icon align-middle text-success icon-mr rounded-circle correct-icon-animate animate-delay"
                        aria-label="Correct selection"
                    ></span>
                    <span
                        v-show="currentChoice && currentChoice.correct === false"
                        class="icon modal-icon icon-glyphicons-basic-circle-empty-remove feedback-icon align-middle text-danger icon-mr incorrect-icon-animate animate-delay"
                        aria-label="Incorrect selection"
                    ></span>
                    <span v-show="currentChoice && currentChoice.text" class="align-middle">{{
                        currentChoice.text
                    }}</span>
                </template>
                <template>
                    <div class="global-theme-text" v-if="currentChoice">
                        <rich-content-display
                            v-if="currentChoice.action == 'show_content_modal'"
                            :elements="currentChoice.elements"
                            :editable="editMode"
                        ></rich-content-display>
                    </div>
                </template>
                <template slot="footer">
                    <button class="btn btn-secondary btn-session" type="button" data-dismiss="modal">
                        {{ labelsTranslated.close }}
                    </button>
                </template>
            </base-modal>
        </div>
        <div class="d-flex align-items-center position-relative mr-1 discovery-readout">
            <div class="flex-grow-1">
                <a v-if="showSetAngleBtn" class="btn btn-link btn-sm p-0" @click="saveInitialCameraAngle()">
                    <span class="icon icon-glyphicons-basic-view-full icon-mr align-middle"></span>
                    <span class="align-middle">Set initial camera angle</span>
                </a>
                <span class="text-secondary ml-2 align-middle" v-show="showLoader">Saving..</span>
            </div>
            <div class="flex-grow-0">
                <div v-if="showDiscoveryMessage">
                    <span class="icon icon-glyphicons-basic-search feedback-icon text-primary icon-mr"></span>
                    <span class="text-secondary" v-if="choices"> {{ discoveryMessage }}</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import http from "@/axios.js"
import { hotspotColors } from "@/assets/js/settings.js"
import BaseModal from "@/components/BaseModal"
import RichContentDisplay from "@/components/RichContentDisplay"
import throttle from "lodash.throttle"
import Hotspots360 from "@/components/Hotspots360"
import InfoPopover from "@/components/InfoPopover"
import SessionFooterBtns from "@/components/SessionFooterBtns"

export default {
    name: "ImageHotspotsPanel",
    components: {
        Hotspots360,
        BaseModal,
        RichContentDisplay,
        InfoPopover,
        SessionFooterBtns,
    },
    props: {
        panel: {
            type: Object,
            required: true,
        },
        sessionId: {
            type: [String, Number],
            required: true,
        },
        savedResponses: {
            type: Array,
            required: false,
        },
        showContinueButton: {
            type: Boolean,
            required: false,
            default: false,
        },
        isPreviouslyVisited: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    data() {
        return {
            markerColors: hotspotColors,
            currentChoice: {
                correct: null,
            },
            contentReady: false,
            resizeWindowHandler: null,
            showFullScreenControls: true,
            imageWidth: null,
            imageHeight: null,
            showLoader: false,
        }
    },
    watch: {
        correctChoicesCount(val) {
            this.$emit("update:correctChoicesCount", val)
        },
        panelID() {
            if (!this.contentIs360) {
                this.initialize2DHotspotPositions()
            }
        },
    },
    computed: {
        loading() {
            return this.$store.getters.loading
        },
        choices() {
            return this.panel.choices
        },
        panelID() {
            return this.panel.id
        },
        subheaderIsConfigured() {
            return this.panel.sub_header !== "" && this.panel.sub_header != "<p></p>"
        },
        correctChoicesCount() {
            return this.panel.choices.filter((c) => c.correct && c.isDiscovered).length
        },
        choiceDiscoveredCount() {
            if (this.panel.choice_count_readout_mode === "correct_only") {
                return this.panel.choices.filter((c) => c.isDiscovered && c.correct).length
            }
            return this.panel.choices.filter((c) => c.isDiscovered).length
        },
        discoverableChoiceCount() {
            if (this.panel.choice_count_readout_mode === "correct_only") {
                return this.panel.correct_choice_count
            }
            return this.panel.choices.length
        },
        discoveryMessage() {
            return `${this.choiceDiscoveredCount} of ${this.discoverableChoiceCount} ${this.labelsTranslated.hotspotsExplored}`
        },
        showDiscoveryMessage() {
            return this.panel.indicate_selected_choices && this.panel.panel_type !== "3d_video"
        },
        showDockedCard() {
            return (
                this.panel.display_full_screen &&
                (this.panel.header || this.subheaderIsConfigured || this.showDiscoveryMessage || this.editMode || this.showContinueButton)
            )
        },
        showSetAngleBtn() {
            return this.editMode && this.panelIsConfigured && this.contentIs360
        },
        contentIs360() {
            return this.panel.hotspots_content_type == "360_image" || this.panel.panel_type === "3d_video"
        },
        fileElement() {
            if (!this.panelIsConfigured) {
                return null
            }
            return this.panel.elements[0]
        },
        fileSource() {
            if (!this.fileElement) {
                return null
            }
            return this.fileElement.stored_file.signed_url
        },
        hotspotsRef() {
            return this.$refs["hotspots360" + this._uid]
        },
        choiceModalSize() {
            if (!this.currentChoice) {
                return ""
            } else if (this.currentChoice.modal_width == "large") {
                return "large"
            } else if (this.currentChoice.modal_width == "extra_large") {
                return "xl"
            }
            return ""
        },
        labelsTranslated() {
            return this.$store.getters.sessionLabelsTranslated
        },
        hotspotTabIndex() {
            return this.panel.enable_hotspot_tabbing ? 0 : -1
        },
        panelIsConfigured() {
            return this.panel && this.panel.elements[0] && this.panel.elements[0].stored_file
        },
        editMode() {
            return this.$store.getters.previewEditMode
        },
        hotspotsContainerStyle() {
            return {
                width: this.panel.display_full_screen && this.imageWidth ? this.imageWidth + "px" : "100%",
                height: this.panel.display_full_screen && this.imageHeight ? this.imageHeight + "px" : "100%",
            }
        },
        zoomLevel() {
            return this.panel.panel_type === "3d_video" ? 3000 : 360
        },
    },
    methods: {
        initPanel() {
            if (this.savedResponses && this.savedResponses.length) {
                let vm = this
                // Update multiple selection panels with choice responses.
                this.savedResponses.forEach(function (savedResponse) {
                    // Find the choice and set its correctness.
                    let choice = vm.choices.find((choice) => choice.id == savedResponse.choice.id)
                    vm.$set(choice, "correct", savedResponse.choice.correct)
                    vm.$set(choice, "isDiscovered", true)
                })
            }
            if (!this.contentIs360) {
                this.initialize2DHotspotPositions()
            }
            this.contentReady = true
        },
        // Initialize the hotspot positioning and size based on the rendered image size.
        initialize2DHotspotPositions() {
            if (!this.fileSource) return
            var img = document.getElementById("hotspots-image" + this._uid)
            let vm = this

            function loaded() {
                // Fetch the width and height of the image.
                let image = document.getElementById("hotspots-image" + vm._uid)
                let imageWidth = parseFloat(image.offsetWidth)
                let imageHeight = parseFloat(image.offsetHeight)

                if (vm.panel.display_full_screen) {
                    // If dispaying in full screen, the image is sized using object-fit: contain.
                    // The code below calculates the resulting width and height of the image, so
                    // we can set make the overlayed hotspots container the same size.
                    const nativeAspectRatio = image.naturalWidth / image.naturalHeight
                    let containerHeight = image.parentElement.offsetHeight
                    let containerWidth = image.parentElement.offsetWidth
                    const containerAspectRatio = containerWidth / containerHeight
                    // Calculate the displayed height and width based on whether the container's width
                    // or height constrains the image.
                    imageWidth =
                        containerAspectRatio > nativeAspectRatio ? containerHeight * nativeAspectRatio : containerWidth
                    imageHeight =
                        containerAspectRatio < nativeAspectRatio ? containerWidth / nativeAspectRatio : containerHeight
                    // Store the dimensions of the image.
                    vm.imageWidth = imageWidth
                    vm.imageHeight = imageHeight
                }

                vm.showSpots = true

                vm.panel.choices.forEach((choice) => {
                    // Scale the x and y position.
                    let xPosition = imageWidth * choice.position_x
                    let yPosition = imageHeight * choice.position_y

                    // Scale the marker radius (and height, for rectangular hotspots).
                    vm.$set(choice, "markerRadius", choice.marker_radius * imageWidth)
                    vm.$set(choice, "markerHeight", choice.marker_height * imageHeight)

                    let markerElement = document.getElementById("hotspot" + choice.id)
                    markerElement.style.transform = `translate(${xPosition}px, ${yPosition}px)`

                    // Initialize popover text if configured.
                    if (choice.show_text_on_hover) {
                        $(markerElement).popover({
                            content: choice.text,
                            trigger: "hover click",
                            placement: "top",
                            template: `
                                <div class="popover hotspot-popover shadow-sm" role="tooltip">
                                    <div class="arrow"></div>
                                    <h3 class="popover-header"></h3>
                                    <div class="popover-body text-center"></div>
                                </div>
                            `,
                        })
                    }
                })
            }

            if (img.complete) {
                loaded()
            } else {
                img.addEventListener("load", loaded)
            }
        },
        // Returns the marker color (as a color name) for a choice.
        getMarkerColor(choice) {
            if (choice.isDiscovered && choice.change_color_when_explored && choice.marker_color_after_explored) {
                return choice.marker_color_after_explored
            }
            return choice.marker_color
        },
        // Returns an array of classes for a hotspot, based on the configuration of
        // the 'choice'.
        choiceClassArray(choice) {
            let classesArray = []
            if (choice.is_clickable && !choice.showLoading) {
                classesArray.push("hotspot-clickable")
            }
            if (choice.marker_visible) {
                classesArray.push("hotspot-visible")
            } else {
                classesArray.push("hotspot-discoverable")
            }
            let markerColor = this.getMarkerColor(choice)
            if (choice.showLoading) {
                classesArray.push(`pulse-${markerColor}-active`)
            } else {
                classesArray.push(`pulse-${markerColor}`)
            }
            if (choice.isDiscovered && this.panel.indicate_selected_choices) {
                classesArray.push("hotspot-discovered")
            }
            classesArray.push(choice.marker_shape == "circle" ? "rounded-circle" : "rounded-sm")
            return classesArray
        },
        // Returns an object used to style a hotspot.
        choiceStyle(choice) {
            let styleObj = {
                width: choice.markerRadius + "px",
                height: choice.marker_shape == "circle" ? choice.markerRadius + "px" : choice.markerHeight + "px",
            }
            let markerColor = this.getMarkerColor(choice)
            if (choice.marker_style == "solid") {
                styleObj["background-color"] = this.markerColors[markerColor]
            } else {
                styleObj["border"] = `solid 3px ${this.markerColors[markerColor]}`
            }
            return styleObj
        },
        // Handles a choice selection - emits the new session object up the parent
        // (the main session component) if the choice results in navigation to a new node.
        selectChoice(choice) {
            if (!choice.is_clickable) {
                if (this.contentIs360) {
                    this.hotspotsRef.removeHotspotLoadingState()
                }
                return
            }

            this.dismissPopovers(null)

            let contentModalWasShown = false
            if (choice.action === "show_content_modal") {
                // The choice action is already loaded, so show the content modal
                // before waiting for the response from the server.
                this.currentChoice = choice
                this.$set(choice, "isDiscovered", true)
                this.showContentModal()
                contentModalWasShown = true
            } else {
                // Reset the current choice to avoid showing the last selected choice
                // to the user before the new one is loaded.
                this.currentChoice = {
                    correct: null,
                }
                this.$set(choice, "showLoading", true)
            }

            let vm = this
            http.post(`/api/students/sessions/${this.sessionId}/branch/${choice.id}/`)
                .then(function (response) {
                    let currentChoice = response.data.choice
                    // Only update the current choice if it's not already loaded.
                    if (!vm.currentChoice.id) {
                        vm.currentChoice = currentChoice
                    }

                    vm.$set(choice, "correct", currentChoice.correct)
                    vm.$set(choice, "action", currentChoice.action)
                    // Only update the elements attached to the choice if they're not already loaded.
                    if (!choice.elements) {
                        vm.$set(choice, "elements", currentChoice.elements)
                    }

                    // Handle choice action.
                    if (currentChoice.action === "show_content_modal" && !contentModalWasShown) {
                        vm.showContentModal()
                    } else if (currentChoice.action === "proceed_to_node") {
                        // Update the session, which includes the new node.
                        vm.$emit("update-session", response.data.session)
                    }

                    // Activate or deactivate vitals triggers included with the payload.
                    let vitalsTriggers = currentChoice.vitals_triggers
                    if (vitalsTriggers.length > 0) {
                        vm.$emit("trigger-vitals", { triggers: vitalsTriggers, activate: true })
                    }

                    vm.$set(choice, "isDiscovered", true)
                    vm.$emit("response-received")
                })
                .finally(function () {
                    choice.showLoading = false
                })
        },
        showContentModal() {
            $("#hotspots-choice-modal" + this._uid).modal("show")
            if (this.contentIs360) {
                this.hotspotsRef.removeHotspotLoadingState()
            }
        },
        dismissPopovers(event) {
            if (event) {
                let clickedOnHotspot = Boolean(event.target.closest(".hotspot"))
                if (clickedOnHotspot) {
                    return
                }
            }
            let popovers = document.getElementsByClassName("hotspot-popover show")
            try {
                for (let element of popovers) {
                    $(element).popover("hide")
                }
            } catch (error) {
                console.log(error)
            }
        },
        dismissPopover(choice) {
            let popover = document.getElementById("hotspot" + choice.id)
            $(popover).popover("hide")
        },
        imageLoaded() {
            this.$emit("image-loaded")
        },
        // Continue to the next node.
        handleContinue() {
            this.$emit("continue")
        },
        toggleFullScreenControls() {
            this.showFullScreenControls = !this.showFullScreenControls
        },
        // This method is used to make 2D hotspots more mobile-friendly. Without tying this handler
        // into the 'touchend' event of hotspots, the default behaviour for non-visible hotspots is
        // for taps to act like a cursor hover. This is confusing to users, because they need to tap twice
        // to get the hotspots action to trigger.
        touchHotspotEnd(event, choice) {
            if (!choice.is_clickable) {
                return
            }
            // let popover = document.getElementById("hotspot" + choice.id)
            // $(popover).popover("show")
            event.preventDefault()
            this.selectChoice(choice)
        },
        // Reset the display of hotspots (in 2D or 360).
        reset() {
            if (this.contentIs360) {
                if (this.hotspotsRef) {
                    this.hotspotsRef.resetScene()
                }
            } else {
                this.initialize2DHotspotPositions()
            }
        },
        // Show the popover label for a choice.
        showChoiceLabel(choice) {
            if (choice.show_text_on_hover) {
                // Show the popover for the choice.
                let popover = document.getElementById("hotspot" + choice.id)
                $(popover).popover("show")
            }
        },
        hideChoiceLabel(choice) {
            if (choice.show_text_on_hover) {
                // Show the popover for the choice.
                let popover = document.getElementById("hotspot" + choice.id)
                $(popover).popover("hide")
            }
        },
        handleEdit() {
            this.$emit("edit")
        },
        moveFullScreenControls(position) {
            this.panel.position = position
            this.$emit("update-field", { position: position })
        },
        // Save the current camera angle as the initial angle for the 360-environment.
        saveInitialCameraAngle() {
            this.showLoader = true
            let vm = this
            let angles = this.hotspotsRef.getCurrentCameraAngle()
            http.put("/api/panels/" + this.panel.id + "/basic/", {
                init_camera_angle_alpha: angles.alpha,
                init_camera_angle_beta: angles.beta,
            })
                .then(function () {
                    vm.$notify({
                        group: "main",
                        type: "success",
                        text: "Initial camera angle saved",
                        data: {},
                    })
                })
                .finally(() => {
                    vm.showLoader = false
                })
        },
        // Handle a 360-degree video ending - called if the media is a 360-degree video.
        handleVideoEnded() {
            this.$emit("update:videoComplete", true)
            if (this.panel.elements[0].continue_after_video_finished) {
                // Signal to the parent component to continue to the next node.
                this.$emit("continue")
            } else {
                // Make sure the user can see other content by exiting full screen.
                this.panel.elements[0].display_full_screen = false
            }
        },
    },
    mounted() {
        let vm = this
        this.$nextTick(function () {
            vm.initPanel()
        })
        // Make sure when the modal is closed, the content is removed. This stops things like
        // audio files and videos from playing the background after the learner has clicked away.
        $("#hotspots-choice-modal" + this._uid).on("hidden.bs.modal", function (event) {
            vm.currentChoice = {
                correct: null,
            }
        })

        let resize = throttle(function () {
            if (!vm.contentIs360) {
                vm.initialize2DHotspotPositions()
            }
        }, 300)
        window.addEventListener("resize", resize)

        // Save the resize function so that we can remove the event listener on exit.
        this.resizeWindowHandler = resize

        // Emit the initial video completion state - used if the media is a 360-degree video.
        if (this.fileElement && this.fileElement.play_video_before_continue) {
            this.$emit("update:videoComplete", false)
        } else {
            this.$emit("update:videoComplete", true)
        }
    },
    beforeDestroy() {
        // Kill all popovers.
        let popovers = document.getElementsByClassName("hotspot-popover")
        try {
            for (let element of popovers) {
                $(element).remove()
            }
        } catch (error) {
            console.log(error)
        }
        // Hide and remove event listeners on the choice modal.
        $("#hotspots-choice-modal" + this._uid).modal("hide")
        $("#hotspots-choice-modal" + this._uid).off()

        window.removeEventListener("resize", this.resizeWindowHandler)
    },
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";
@import "@/assets/styles/hotspots.scss";

$hotspot-opacity: 0.915;

.hotspot-card {
    overflow: hidden;
}

.hotspots-container {
    position: relative;
}

.spots-container {
    position: absolute;
}

.hotspot {
    transform: translate(0px, 0px);
    position: absolute;
    z-index: 15;
}

.hotspot-visible {
    opacity: $hotspot-opacity;
}

.hotspot-clickable {
    cursor: pointer;
}

.hotspot-discoverable {
    opacity: 0;
    transition: opacity ease-in-out 0.15s;
    &:hover {
        opacity: $hotspot-opacity;
    }
}

.hotspot-discovered {
    opacity: 0.65 !important;
}

.full-screen-controls {
    position: fixed;
    height: auto;
    bottom: 0;
    background: #fff;
    z-index: 20;
    border-bottom-right-radius: 0px !important;
    border-bottom-left-radius: 0px !important;
    width: 100%;
    @media (min-width: $breakpoint-sm) {
        width: 576px;
    }
}

.full-screen-controls-left {
    left: 0;
    border-top-left-radius: 0px;
}

.full-screen-controls-right {
    right: 0;
    border-top-right-radius: 0px;
}

.full-screen-2d {
    position: fixed;
    width: 100%;
    height: 100%;
    left: 0;
    top: 0;
    background: #212121;
    z-index: 10;
    max-width: 100%;
    max-height: 100%;
    img {
        object-fit: contain;
        max-height: 100%;
        max-width: 100%;
        width: 100%;
        height: 100%;
    }
}

.controls-collapsed {
    height: 11px;
}

.collapse-chevron {
    padding-left: 2px !important;
    padding-right: 2px !important;
    padding-top: 1px !important;
    padding-bottom: 1px !important;
    margin-top: -10px;
}

.discovery-readout {
    top: 9px;
    font-size: 13px;
    height: 4px;
}
</style>

<style lang="scss">
// Need to keep this class unscoped to work with popover-added classes.
.hotspot-popover {
    z-index: 1040 !important;
}
</style>